import { useCallback, useMemo, useState } from 'react';
import {
  RETIREMENT_CALCULATOR_FIELDS_ORDER,
  RetirementCalculatorField,
} from './RetirementCalculator.const';
import {
  RetirementCalculatorErrorCodesModel,
  RetirementCalculatorFormValuesModel,
  RetirementCalculatorSubmitValuesModel,
} from './RetirementCalculator.model';
import { validateRetirementCalculatorForm } from './RetirementCalculator.utils';

interface UseRetirementCalculatorFormParams {
  initialValues?: RetirementCalculatorFormValuesModel;
  onSubmit?: (values: RetirementCalculatorSubmitValuesModel) => void;
}

export const useRetirementCalculatorForm = ({
  initialValues,
  onSubmit,
}: UseRetirementCalculatorFormParams) => {
  const [values, setValues] = useState<RetirementCalculatorFormValuesModel>(initialValues ?? {});
  const [wasSubmitted, setWasSubmitted] = useState(false);

  const allErrors = useMemo(() => validateRetirementCalculatorForm(values), [values]);
  const errors: RetirementCalculatorErrorCodesModel = wasSubmitted ? allErrors : {};

  const setFieldValue = useCallback(
    <TField extends RetirementCalculatorField>(
      field: TField,
      value: RetirementCalculatorFormValuesModel[TField],
    ) => {
      setValues(previousValues => ({ ...previousValues, [field]: value }));
    },
    [],
  );

  const submit = useCallback((): RetirementCalculatorField | undefined => {
    setWasSubmitted(true);

    const firstInvalidField = RETIREMENT_CALCULATOR_FIELDS_ORDER.find(field => allErrors[field]);
    if (firstInvalidField) {
      return firstInvalidField;
    }

    onSubmit?.(values as RetirementCalculatorSubmitValuesModel);
    return undefined;
  }, [allErrors, onSubmit, values]);

  return { values, errors, setFieldValue, submit };
};
